import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ONBOARDING_V2 } from './onboarding/onboardingV2Routes';
import { cuestionarioCompletado, debeIrAlCuestionario } from '../lib/cuestionarioObligatorio';
import { leerCuestionarioEnLaBase } from '../lib/cuestionarioEnLaBase';
import { trackEvent } from '../lib/posthog';

/**
 * Manda al cuestionario, en cada cambio de ruta, a quien lo tiene pendiente.
 *
 * Es la mitad que le faltaba al rebote de `App`: ése corre en eventos de sesión y no ve
 * una navegación SPA, así que cualquier salida de la cadena post-pago que cayera en la
 * app dejaba adentro, sin cuestionario, a quien acababa de pagar por el alta por link.
 * Va montado junto a `ScrollToTop`, que es el otro componente que mira cada ruta.
 * Ver ADR-2026-09-23-el-cuestionario-es-obligatorio-tambien-al-navegar.
 *
 * El flag lo prende `checkUserProfile` con una lectura positiva del centinela y lo apaga
 * el cuestionario al escribir el rubro. Las rutas exentas son las mismas del rebote
 * (`rutaExentaDelCuestionario`), incluido el cuestionario mismo.
 *
 * **Antes de sacar a nadie, confirma en la base** (ADR-2026-10-20-el-onboarding-no-rebota-con-una-lectura-vieja).
 * El flag vive en memoria y una lectura que llegó tarde lo dejaba prendido de más: en
 * Android, dentro de Instagram o Facebook, la app devolvía al onboarding a gente que lo
 * acababa de terminar, a veces tres o cuatro veces seguidas. Con la lectura fresca:
 *  · si ya terminó, apaga el flag y no la mueve (y lo registra, para medir);
 *  · si de verdad le falta, la manda al cuestionario como siempre;
 *  · si no se pudo leer, no la mueve — misma regla que el rebote: un error nunca saca a
 *    nadie de donde está. El flag queda prendido y se vuelve a confirmar en la próxima
 *    navegación.
 * La consulta corre sólo con el flag prendido, o sea casi nunca.
 */
export const GuardiaDelCuestionario = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!debeIrAlCuestionario(pathname)) return;
    // Si la persona navega mientras se lee, la respuesta de esta ruta ya no decide nada:
    // la ruta nueva corre su propio efecto.
    let vigente = true;
    void leerCuestionarioEnLaBase().then((enLaBase) => {
      if (!vigente) return;
      if (enLaBase === 'completo') {
        cuestionarioCompletado();
        trackEvent('rebote_onboarding_evitado', { motivo: 'guardia', ruta: pathname });
        return;
      }
      if (enLaBase === 'no_se_sabe') return;
      // Pudo haberse apagado mientras se leía (el cuestionario escribió el rubro).
      if (!debeIrAlCuestionario(pathname)) return;
      trackEvent('rebote_al_onboarding', { ruta: pathname });
      navigate(ONBOARDING_V2.setup, { replace: true });
    });
    return () => {
      vigente = false;
    };
  }, [pathname, navigate]);

  return null;
};
